import React from 'react'
import { Grid, Typography, Alert, CircularProgress } from '@the-platform-group/component-library'
import { usePeopleMergeContext } from 'pages/MergeProfiles/contexts/PeopleMergeContext'
import { usePreviewMergeDrawers } from 'pages/MergeProfiles/hooks/usePreviewMergeDrawers'
import MergedProfilePreview from './MergedProfilePreview'
import MappedJobsSection from './MappedJobsSection'
import JobMapDrawer from '../MultipleJobsSection/JobMapDrawer'
import JobPreviewDrawer from '../JobPreviewDrawer'

/**
 * PreviewMergeSection - Final review section before submitting the merge (Step 3)
 * This is the final step where users review the complete merge result before submission.
 * It shows:
 * - **MergedProfilePreview:** Person-level fields (name, emails, phones, etc.)
 * - **MappedJobsSection:** All job mappings with override details
 * - **Alerts:** Warnings if primary job changed or overrides were applied
 * @returns {React.Component} The complete preview section with profile, jobs, and drawers
 */
const PreviewMergeSection = () => {
  const { mergeData, jobMappingData, reviewMutation } = usePeopleMergeContext()
  const { sourcePerson, targetPerson, sourceId, targetId } = mergeData
  const { hasOverrides, jobMappings } = jobMappingData
  const { data: reviewData, fetchReview, isLoading: isReviewLoading } = reviewMutation

  const primaryJobChanged = reviewData?.meta?.review?.primaryJobChanged

  const {
    isMapDrawerOpen,
    isDetailsDrawerOpen,
    selectedJobs,
    selectedTargetJobId,
    handleEditMapping,
    handleCloseMapDrawer,
    handleMappingSaved,
    handleViewDetails,
    handleCloseDetailsDrawer,
  } = usePreviewMergeDrawers({
    fetchReview,
    sourceId,
    targetId,
    jobMappings,
  })

  if (!sourcePerson || !targetPerson) {
    return null
  }

  return (
    <Grid container direction="column" spacing={2}>
      <Grid item>
        <Typography variant="body2" color="text.secondary">
          Please review the merge details below for the final merged profile:
        </Typography>
      </Grid>

      {(hasOverrides || primaryJobChanged) && (
        <Grid item>
          <Alert severity="info" sx={{ alignItems: 'center' }}>
            {primaryJobChanged
              ? 'Primary job has been changed based on the job mapping.'
              : 'Primary profile details have been updated based on the new job mapping.'}
          </Alert>
        </Grid>
      )}

      <Grid item sx={{ position: 'relative' }}>
        {isReviewLoading && (
          <Grid
            container
            justifyContent="center"
            alignItems="center"
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'grey.100',
              opacity: 0.9,
              zIndex: 1,
            }}
          >
            <CircularProgress />
          </Grid>
        )}
        <MergedProfilePreview />
      </Grid>

      <Grid item>
        <MappedJobsSection onEditMapping={handleEditMapping} onViewDetails={handleViewDetails} />
      </Grid>

      <JobMapDrawer
        open={isMapDrawerOpen}
        onClose={handleCloseMapDrawer}
        selectedJobs={selectedJobs}
        onSave={handleMappingSaved}
      />

      <JobPreviewDrawer
        open={isDetailsDrawerOpen}
        onClose={handleCloseDetailsDrawer}
        selectedTargetJobId={selectedTargetJobId}
      />
    </Grid>
  )
}

export default PreviewMergeSection
